import * as React from "react"
import { tradeBySlug } from "../../data/trades"
import { INSTALLED_SITE_TEMPLATES } from "../../data/site-templates"
import { activeExtras, activeTemplate, showsBranding, themeById } from "../../lib/site-draft"
import { LEAD_ENDPOINT, LEAD_LIMITS } from "../../lib/lead-endpoint"
import { slugify } from "../../lib/slug"
import { stateByCode } from "../../data/us-states"
import { formatPhone, telHref } from "../../lib/phone"
import TradeIcon from "./TradeIcon"
import TemplateV2 from "./templates/TemplateV2"
import TemplateV3 from "./templates/TemplateV3"
import TemplateClean from "./templates/TemplateClean"
import TemplateStudio from "./templates/TemplateStudio"
import TemplateSignature from "./templates/TemplateSignature"
import * as s from "./GeneratedSite.module.css"

/**
 * THE GENERATED WEBSITE.
 * ============================================================================
 * The actual contractor site, rendered from the draft. Not a picture of one
 * and not an approximation: this component is what the preview iframe shows,
 * what the download produces and what gets published — one implementation, so
 * what the contractor approved is literally what goes live.
 *
 * ── IT IS RESPONSIVE, NOT TWO CANVASES ─────────────────────────────────────
 * Fluid width with real `@media` breakpoints. The preview renders it inside an
 * iframe sized to a device so those breakpoints actually fire, which is why
 * the phone view is a real phone render rather than the desktop squashed.
 *
 * ── LINKS ARE REAL ─────────────────────────────────────────────────────────
 * The phone number is a `tel:` link and the email an anchor, because this is a
 * website somebody's customers will use on a phone. A preview that renders
 * inert spans and a published page that renders links are two implementations
 * waiting to disagree, so there is only the one.
 *
 * ── IMAGE SLOTS ARE PANELS UNTIL THEY ARE PHOTOGRAPHS ──────────────────────
 * An empty slot paints a branded gradient carrying the trade's glyph. It does
 * NOT reach for stock photography: a stock roof published as this contractor's
 * work misrepresents their portfolio. Their own pictures replace the panels,
 * and until then the panels look deliberate.
 */

/**
 * `slot` names the picture — MTC-FCS-027.
 *
 * It rides along as a data attribute so a click inside the PREVIEW can say
 * which image was clicked. The published page carries it too and is none the
 * worse for it: it is an inert attribute, no script reads it there, and the
 * alternative — a second render of the site with editing hooks in it — is two
 * implementations waiting to disagree.
 */
const Slot = ({ src, tone, icon, alt, className, slot }) => {
  if (src) {
    return (
      <div className={`${s.slot} ${className || ""}`} data-slot={slot}>
        <img src={src} alt={alt || ""} />
      </div>
    )
  }
  return (
    <div
      className={`${s.slot} ${s[tone] || s.toneA} ${className || ""}`}
      data-slot={slot}
      aria-hidden="true"
    >
      <TradeIcon name={icon} className={s.slotGlyph} />
    </div>
  )
}

/**
 * The "where we work" line — MTC-FCS-026, now across states.
 *
 * Whole states are named first because they are the bigger claim, then the
 * counties. Both are truncated: a list of forty county names is not a sentence
 * anybody reads, and the exact list is on the contact section anyway.
 */
export function areaLineFor(draft) {
  const areas = draft.areas || {}
  const whole = (areas.wholeStates || [])
    .map(code => (stateByCode(code) || {}).name || code)
    .filter(Boolean)
  const counties = areas.countyNames || []

  const join = list =>
    list.length <= 1
      ? list.join("")
      : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`

  if (whole.length > 0 && counties.length === 0) {
    return `Serving all of ${join(whole)}`
  }

  const shownCounties =
    counties.length > 4
      ? `${counties.slice(0, 4).join(", ")} and ${counties.length - 4} more`
      : join(counties)

  if (whole.length === 0) {
    return shownCounties ? `Serving ${shownCounties}` : ""
  }
  return `Serving all of ${join(whole)}, plus ${shownCounties}`
}

/** The ported designs, by template id — see data/site-templates.js. */
const PORTED = {
  "mm-v2": TemplateV2,
  "mm-v3": TemplateV3,
  "mm-clean": TemplateClean,
  "mm-studio": TemplateStudio,
  "mm-signature": TemplateSignature,
  ...Object.fromEntries(INSTALLED_SITE_TEMPLATES.map(template => [template.id, template.Component])),
}

/**
 * Which source the content comes from — FCS-057(b).
 *
 * "local"  the draft handed in, which is the builder's preview and the
 *          template's own sample content when there is nothing else
 * "remote" a published site, fetched from Storage and already validated by
 *          lib/site-content.js before it ever reaches here
 *
 * Local is the default, deliberately: everything that renders a site today is
 * the builder, and only components/site/RemoteSite.js asks for remote. The
 * mode is carried down to the templates so each design can tell the two apart
 * without inspecting the data and guessing.
 */
export const DEFAULT_MODE = "local"

const GeneratedSite = ({ draft, plan, mode = DEFAULT_MODE }) => {
  /* ── Ported designs render themselves — MTC-FCS-041 ──────────────────
     The Mockup Maker's templates are real designs with their own markup and
     their own stylesheet, not a restyling of the one below. A template marked
     `ported` in the registry is handed the draft and renders the whole page.

     The premium gate still applies first: activeTemplate() falls back to the
     free template when the plan does not cover the chosen one, so a Freemium
     payment cannot reach this branch carrying a premium design. */
  const chosen = activeTemplate(draft, plan || draft.plan)
  const Ported = PORTED[chosen.id]
  if (Ported) return <Ported draft={draft} plan={plan} mode={mode} />

  const trade = tradeBySlug(draft.tradeSlug)
  const theme = themeById(draft.themeId)
  const services = draft.services.filter(Boolean).slice(0, 3)
  const icon = trade ? trade.icon : "building"

  const name = draft.business.name.trim() || "Your Business Name"
  // Which published site a lead belongs to. `draft.slug` is the address chosen
  // at checkout and is the authority once it exists; before that the derived
  // one keeps the preview's form pointing somewhere sensible.
  const siteSlug = draft.slug || slugify(draft.business.name)
  const tagline = draft.business.tagline.trim() || (trade ? trade.description : "")
  // Shown as (###) ###-####; the tel: link carries digits. See lib/phone.js.
  const phone = formatPhone(draft.business.phone)
  const email = draft.business.email.trim()
  const tel = telHref(phone)
  const areaLine = areaLineFor(draft)

  /* Premium sections — MTC-FCS-015. `plan` is passed in by the publish path,
     which takes it from the paid Stripe session rather than from the draft;
     everywhere else it is the draft's own choice, which is what the preview
     should show. Each one carries data-premium so the publish route can lift
     them back out if the payment says Freemium. */
  const extras = activeExtras(draft, plan || draft.plan)
  // One attribute selects the arrangement — see data/site-templates.js for
  // why it is an attribute rather than four components.
  const template = activeTemplate(draft, plan || draft.plan)
  // Same single-attribute trick as the template, for the same reason: undoing
  // it on the publish path is one rewrite rather than surgery on a footer.
  const branding = showsBranding(draft, plan || draft.plan) ? "on" : "off"
  const gallery = (draft.gallery || []).filter(Boolean).slice(0, 6)
  const reviews = (draft.reviews || []).filter(r => r && r.quote.trim()).slice(0, 6)
  const faqs = (draft.faqs || []).filter(f => f && f.q.trim() && f.a.trim()).slice(0, 8)
  const hours = (draft.hours || []).filter(h => h && h.text.trim())

  return (
    <div
      className={s.site}
      data-template={template.id}
      data-branding={branding}
      style={{ "--t-primary": theme.primary, "--t-accent": theme.accent, "--t-ink": theme.ink }}
    >
      <header className={s.nav}>
        <div className={s.brand}>
          <span className={s.brandMark}>
            <TradeIcon name={icon} />
          </span>
          <span className={s.brandName}>{name}</span>
        </div>
        <nav className={s.navLinks}>
          <a href="#top">Home</a>
          <a href="#services">Services</a>
          {/* Only for sections that are actually on the page — a nav link to
              an anchor that does not exist scrolls nowhere and looks broken. */}
          {extras.gallery ? <a href="#gallery">Our Work</a> : null}
          {extras.reviews ? <a href="#reviews">Reviews</a> : null}
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
        {tel ? (
          <a className={s.navPhone} href={tel}>
            {phone}
          </a>
        ) : null}
      </header>

      <section className={s.hero} id="top">
        <Slot src={draft.images.hero} tone="toneA" icon={icon} className={s.heroSlot} slot="hero" />
        <div className={s.heroScrim} />
        <div className={s.heroCopy}>
          <div className={s.heroInner}>
            {trade ? <p className={s.heroKicker}>{trade.name}</p> : null}
            <h1 className={s.heroTitle}>{name}</h1>
            {tagline ? <p className={s.heroLead}>{tagline}</p> : null}
            <div className={s.heroActions}>
              <a className={s.btnPrimary} href="#contact">
                Get a Free Quote
              </a>
              {tel ? (
                <a className={s.btnGhost} href={tel}>
                  Call {phone}
                </a>
              ) : null}
            </div>
            {areaLine ? <p className={s.heroArea}>{areaLine}</p> : null}
          </div>
        </div>
      </section>

      {services.length > 0 && (
        <section className={s.services} id="services">
          <h2 className={s.sectionTitle}>What We Do</h2>
          <div className={s.serviceGrid}>
            {services.map((title, i) => (
              <article key={title} className={s.serviceCard}>
                <Slot
                  src={draft.images[`service${i + 1}`]}
                  tone={["toneA", "toneB", "toneC"][i]}
                  icon={icon}
                  className={s.serviceSlot}
                  slot={`service${i + 1}`}
                />
                <div className={s.serviceBody}>
                  <h3>{title}</h3>
                  <p>{draft.serviceCopy[title] || ""}</p>
                  <a className={s.serviceLink} href="#contact">
                    Get a quote →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {extras.gallery && (
        <section className={s.gallery} id="gallery" data-premium="gallery">
          <h2 className={s.sectionTitle}>Our Work</h2>
          <div className={s.galleryGrid}>
            {gallery.map((src, i) => (
              // Decorative in the sense that matters for alt text: the page
              // says nothing about what any individual photograph shows, so
              // inventing a description here would be worse than an empty one.
              <img key={i} src={src} alt="" className={s.galleryShot} />
            ))}
          </div>
        </section>
      )}

      {extras.reviews && (
        <section className={s.reviews} id="reviews" data-premium="reviews">
          <h2 className={s.sectionTitle}>What Our Customers Say</h2>
          <div className={s.reviewGrid}>
            {reviews.map((review, i) => (
              <figure key={i} className={s.review}>
                <blockquote>{review.quote}</blockquote>
                {review.name.trim() ? <figcaption>{review.name}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>
      )}

      {draft.business.about.trim() && (
        <section className={s.about} id="about">
          <Slot src={draft.images.about} tone="toneB" icon={icon} className={s.aboutSlot} slot="about" />
          <div className={s.aboutCopy}>
            <h2 className={s.sectionTitle}>About Us</h2>
            <p>{draft.business.about}</p>
            {areaLine ? <p className={s.aboutArea}>{areaLine}</p> : null}
          </div>
        </section>
      )}

      {extras.faqs && (
        <section className={s.faqs} id="faqs" data-premium="faqs">
          <h2 className={s.sectionTitle}>Questions We Get Asked</h2>
          <div className={s.faqList}>
            {faqs.map((faq, i) => (
              <div key={i} className={s.faq}>
                <h3>{faq.q}</h3>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {extras.hours && (
        <section className={s.hours} id="hours" data-premium="hours">
          <h2 className={s.sectionTitle}>Opening Hours</h2>
          <dl className={s.hoursList}>
            {hours.map(row => (
              <div key={row.day} className={s.hoursRow}>
                <dt>{row.day}</dt>
                <dd>{row.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {extras.leadform && (
        <section className={s.quote} id="quote" data-premium="leadform">
          <h2 className={s.sectionTitle}>Request a Quote</h2>
          <p className={s.quoteLead}>
            Tell us what you need and we will come back to you with a price.
          </p>
          {/* A real form, posting to a real endpoint on the same origin the
              published page is served from. The page ships no JavaScript, so
              this is a plain POST and the server answers with a page — which
              also means it still works with scripting off. */}
          <form className={s.quoteForm} method="post" action={LEAD_ENDPOINT}>
            <input type="hidden" name="site" value={siteSlug} />
            <label>
              <span>Your name</span>
              <input name="name" autoComplete="name" required maxLength={LEAD_LIMITS.name} />
            </label>
            <label>
              <span>Phone</span>
              <input name="phone" type="tel" autoComplete="tel" maxLength={LEAD_LIMITS.phone} />
            </label>
            <label>
              <span>Email</span>
              <input name="email" type="email" autoComplete="email" maxLength={LEAD_LIMITS.email} />
            </label>
            <label className={s.quoteWide}>
              <span>What do you need?</span>
              {/* The contractor's own service list, so the enquiry arrives
                  saying which job it is about — MTC-FCS-017. */}
              <select name="service">
                {(services.length > 0 ? services : [trade ? trade.name : "General work"]).map(
                  title => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  )
                )}
              </select>
            </label>
            <label className={s.quoteWide}>
              <span>Project details</span>
              <textarea name="message" rows={4} maxLength={LEAD_LIMITS.message} />
            </label>
            <button type="submit" className={`${s.btnPrimary} ${s.quoteSubmit}`}>
              Send my request
            </button>
          </form>
        </section>
      )}

      <section className={s.contact} id="contact">
        <h2>Get in touch</h2>
        <p className={s.contactLead}>Tell us what you need and we will get back to you.</p>
        <div className={s.contactRow}>
          {phone ? (
            <span className={s.contactItem}>
              <strong>Phone</strong>
              <a href={tel}>{phone}</a>
            </span>
          ) : null}
          {email ? (
            <span className={s.contactItem}>
              <strong>Email</strong>
              <a href={`mailto:${email}`}>{email}</a>
            </span>
          ) : null}
          {areaLine ? (
            <span className={s.contactItem}>
              <strong>Service area</strong>
              {areaLine.replace(/^Serving /, "")}
            </span>
          ) : null}
        </div>
        {tel ? (
          /* "Call Now", not "Call <number>": the number is already printed
             three lines above this button, and a contact block that says it
             twice reads like a mistake rather than an invitation. The hero
             button keeps the number, because nothing near it has said it. */
          <a className={s.btnPrimary} href={tel}>
            Call Now
          </a>
        ) : email ? (
          <a className={s.btnPrimary} href={`mailto:${email}`}>
            Email us
          </a>
        ) : null}
      </section>

      <footer className={s.footer}>
        <span>
          © {new Date().getFullYear()} {name}
        </span>
        {/* Hidden by CSS on data-branding="off" rather than removed from the
            markup, so putting it back is the same one-attribute rewrite the
            template uses. */}
        <span className={s.footerCredit}>
          Website by{" "}
          <a href="https://freecontractorservices.com">FreeContractorServices.com</a>
        </span>
      </footer>
    </div>
  )
}

export default GeneratedSite
